import classNames from 'classnames';
import {
  type AnchorHTMLAttributes,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type Ref,
} from 'react';

import { mergeRefs } from '../../utils/merge-refs';
import styles from './Link.module.css';

export type LinkColor = 'inherit' | 'normal' | 'accent';
/** Underline shown on hover, at rest only, always, or never. */
export type LinkUnderline = 'hover' | 'rest' | 'always' | 'none';

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'> {
  color?: LinkColor;
  underline?: LinkUnderline;
  /** Merge styling onto the single child element (e.g. a router link) instead of an <a>. */
  asChild?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    children,
    color = 'inherit',
    underline = 'hover',
    asChild = false,
    className,
    ...rest
  },
  ref
) {
  const rootClassName = classNames(
    styles.link,
    styles[color],
    styles[underline],
    className
  );

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{ className?: string }>;
    const childRef = (child as ReactElement & { ref?: Ref<HTMLAnchorElement> })
      .ref;
    return cloneElement(child, {
      ...rest,
      ...(ref ? { ref: mergeRefs([ref, childRef]) } : {}),
      className: classNames(rootClassName, child.props.className),
    } as Partial<typeof child.props>);
  }

  return (
    <a ref={ref} className={rootClassName} {...rest}>
      {children}
    </a>
  );
});
